// =============================================
// COMPONENTE SIDEBAR - MENU LATERAL
// =============================================
// Arquivo: Sidebar.tsx
// Descrição: Componente de menu lateral responsivo com navegação
// =============================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks';
import { SystemModule } from '../../types/permissions';
import './Sidebar.css';

// =============================================
// INTERFACES
// =============================================

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  submenu?: SubMenuItem[];
  module?: SystemModule; // Módulo de permissão
  requireAdmin?: boolean; // Requer admin
}

interface SubMenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  module?: SystemModule; // Módulo de permissão
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}

// =============================================
// DADOS DO MENU
// =============================================

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'fas fa-chart-line',
    path: '/dashboard',
    module: SystemModule.DASHBOARD
  },
  {
    id: 'conferencia',
    label: 'Conferência Carga',
    icon: 'fas fa-box-open',
    submenu: [
      {
        id: 'dashboard-operacional',
        label: 'Dashboard Operacional',
        icon: 'fas fa-chart-line',
        path: '/dashboard-operacional',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'etiquetas',
        label: 'Pedidos Importados',
        icon: 'fas fa-barcode',
        path: '/pedidos',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'montagem-paletes',
        label: 'Montar Carga',
        icon: 'fas fa-pallet',
        path: '/montar-carga',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'paletes',
        label: 'Paletes',
        icon: 'fas fa-boxes-stacked',
        path: '/montar-carga/paletes',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'conferencias',
        label: 'Conferências',
        icon: 'fas fa-clipboard-check',
        path: '/conferencia',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'nao-conformidades',
        label: 'Não Conformidades',
        icon: 'fas fa-exclamation-triangle',
        path: '/nao-conformidades',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'relatorios-conferencia',
        label: 'Relatórios',
        icon: 'fas fa-file-alt',
        path: '/conferencia/relatorios',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'clientes',
        label: 'Clientes',
        icon: 'fas fa-building',
        path: '/clientes',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'montagem-paletes-areas',
        label: 'Áreas de Entrega',
        icon: 'fas fa-map-marker-alt',
        path: '/montar-carga/areas',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'tipos-ocorrencia-divergencia',
        label: 'Ocorrências',
        icon: 'fas fa-tags',
        path: '/tipos-ocorrencia-divergencia',
        module: SystemModule.CONFERENCIA_DIVERGENCIAS
      },
      {
        id: 'conferentes',
        label: 'Conferentes',
        icon: 'fas fa-id-badge',
        path: '/conferentes',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'montagem-paletes-filiais',
        label: 'Filiais/Unidades',
        icon: 'fas fa-building',
        path: '/montar-carga/filiais',
        module: SystemModule.CONFERENCIA_CARGA
      },
      {
        id: 'config-conferencia',
        label: 'Configurações',
        icon: 'fas fa-cog',
        path: '/conferencia/configuracoes',
        module: SystemModule.CONFERENCIA_CARGA
      },
    ]
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    icon: 'fas fa-plus-circle',
    submenu: [
      {
        id: 'obras',
        label: 'Obras',
        icon: 'fas fa-building',
        path: '/obras',
        module: SystemModule.OBRAS
      },
      {
        id: 'veiculos',
        label: 'Veículos',
        icon: 'fas fa-truck',
        path: '/veiculos',
        module: SystemModule.VEICULOS
      },
      {
        id: 'motoristas',
        label: 'Motoristas',
        icon: 'fas fa-users',
        path: '/motoristas',
        module: SystemModule.MOTORISTAS
      },
      {
        id: 'equipamentos',
        label: 'Equipamentos',
        icon: 'fas fa-tools',
        path: '/equipamentos',
        module: SystemModule.EQUIPAMENTOS
      },
      {
        id: 'itens-checklist',
        label: 'Itens de Checklist',
        icon: 'fas fa-list-check',
        path: '/itens-checklist',
        module: SystemModule.CHECKLISTS
      },
      {
        id: 'grupos-checklist',
        label: 'Grupos de Checklist',
        icon: 'fas fa-layer-group',
        path: '/grupos-checklist',
        module: SystemModule.GRUPOS_CHECKLIST
      },
      {
        id: 'ocorrencias',
        label: 'Ocorrências',
        icon: 'fas fa-exclamation-triangle',
        path: '/ocorrencias',
        module: SystemModule.CHECKLISTS
      }
    ]
  },
  {
    id: 'checklists',
    label: 'Checklists',
    icon: 'fas fa-clipboard-list',
    path: '/checklists',
    module: SystemModule.CHECKLISTS
  },
  {
    id: 'abastecimentos',
    label: 'Abastecimentos',
    icon: 'fas fa-gas-pump',
    path: '/abastecimentos',
    module: SystemModule.ABASTECIMENTOS
  },
  {
    id: 'agendamentos',
    label: 'Agendamentos',
    icon: 'fas fa-calendar-alt',
    path: '/agendamentos',
    module: SystemModule.AGENDAMENTOS
  },
  {
    id: 'produtividade',
    label: 'Produtividade',
    icon: 'fas fa-chart-bar',
    path: '/produtividade',
    module: SystemModule.PRODUTIVIDADE
  },
  {
    id: 'manutencoes',
    label: 'Manutenções',
    icon: 'fas fa-wrench',
    submenu: [
      {
        id: 'manutencoes-preventivas',
        label: 'Preventivas',
        icon: 'fas fa-shield-alt',
        path: '/manutencoes-preventivas',
        module: SystemModule.MANUTENCOES_PREVENTIVAS
      },
      {
        id: 'manutencoes-corretivas',
        label: 'Corretivas',
        icon: 'fas fa-tools',
        path: '/manutencoes-corretivas',
        module: SystemModule.MANUTENCOES_CORRETIVAS
      },
      {
        id: 'pneus',
        label: 'Pneus',
        icon: 'fas fa-circle-notch',
        path: '/pneus',
        module: SystemModule.PNEUS
      },
      {
        id: 'rodizio-recapagem',
        label: 'Rodizio / Recapagem',
        icon: 'fas fa-sync-alt',
        path: '/pneus/rodizio-recapagem',
        module: SystemModule.RODIZIO_RECAPAGEM
      }
    ]
  },
  {
    id: 'estoque-compras',
    label: 'Estoque / Compras',
    icon: 'fas fa-boxes',
    submenu: [
      {
        id: 'estoque-pecas',
        label: 'Estoque de Pecas',
        icon: 'fas fa-cogs',
        path: '/estoque',
        module: SystemModule.ESTOQUE
      },
      {
        id: 'compras-nfe',
        label: 'Compras / NF-e',
        icon: 'fas fa-file-invoice',
        path: '/compras',
        module: SystemModule.COMPRAS
      }
    ]
  },
  {
    id: 'multas-sinistros',
    label: 'Multas / Sinistros',
    icon: 'fas fa-gavel',
    submenu: [
      {
        id: 'multas',
        label: 'Multas',
        icon: 'fas fa-exclamation-triangle',
        path: '/multas',
        module: SystemModule.MULTAS
      },
      {
        id: 'sinistros',
        label: 'Sinistros',
        icon: 'fas fa-car-crash',
        path: '/sinistros',
        module: SystemModule.SINISTROS
      }
    ]
  },
  {
    id: 'tacografos',
    label: 'Tacografos',
    icon: 'fas fa-tachometer-alt',
    path: '/tacografos',
    module: SystemModule.TACOGRAFOS
  },
  {
    id: 'dashboard-combustivel',
    label: 'Dashboard Combustivel',
    icon: 'fas fa-gas-pump',
    path: '/dashboard-combustivel',
    module: SystemModule.DASHBOARD_COMBUSTIVEL
  },
  {
    id: 'scoring',
    label: 'Scoring Motoristas',
    icon: 'fas fa-star',
    path: '/scoring',
    module: SystemModule.SCORING_MOTORISTAS
  },
  {
    id: 'telematica',
    label: 'Telematica',
    icon: 'fas fa-satellite',
    path: '/telematica',
    module: SystemModule.TELEMATICA
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: 'fas fa-shield-alt',
    path: '/compliance',
    module: SystemModule.COMPLIANCE
  },
  {
    id: 'tco',
    label: 'TCO (Custos)',
    icon: 'fas fa-dollar-sign',
    path: '/tco',
    module: SystemModule.TCO
  },
  {
    id: 'automacao',
    label: 'Automação',
    icon: 'fas fa-robot',
    submenu: [
      {
        id: 'editor-fluxos',
        label: 'Editor de Fluxos',
        icon: 'fas fa-project-diagram',
        path: '/automacoes',
        module: SystemModule.AUTOMACAO_EDITOR
      },
      {
        id: 'simulador-fluxos',
        label: 'Simulador de Fluxos',
        icon: 'fas fa-flask',
        path: '/automacoes/simulador',
        module: SystemModule.AUTOMACAO_SIMULADOR
      },
      {
        id: 'execucoes-automacao',
        label: 'Execuções',
        icon: 'fas fa-play-circle',
        path: '/automacoes/execucoes',
        module: SystemModule.AUTOMACAO_EDITOR
      }
    ]
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    icon: 'fas fa-file-alt',
    submenu: [
      {
        id: 'relatorio-produtividade-equipamentos',
        label: 'Produtividade de Equipamentos',
        icon: 'fas fa-tachometer-alt',
        path: '/relatorios/produtividade-equipamentos',
        module: SystemModule.RELATORIO_PRODUTIVIDADE_EQUIPAMENTOS
      },
      {
        id: 'relatorio-checklists',
        label: 'Checklists',
        icon: 'fas fa-clipboard-list',
        path: '/relatorios/checklists',
        module: SystemModule.RELATORIO_CHECKLISTS
      },
      {
        id: 'relatorio-veiculos',
        label: 'Veículos',
        icon: 'fas fa-truck',
        path: '/relatorios/veiculos',
        module: SystemModule.RELATORIO_VEICULOS
      },
      {
        id: 'relatorio-ocorrencias',
        label: 'Ocorrências',
        icon: 'fas fa-exclamation-circle',
        path: '/relatorios/ocorrencias',
        module: SystemModule.RELATORIO_OCORRENCIAS
      },
      {
        id: 'relatorio-abastecimentos',
        label: 'Abastecimentos',
        icon: 'fas fa-gas-pump',
        path: '/relatorios/abastecimentos',
        module: SystemModule.ABASTECIMENTOS
      }
    ]
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    icon: 'fas fa-cog',
    submenu: [
      {
        id: 'usuarios',
        label: 'Usuários',
        icon: 'fas fa-user-cog',
        path: '/configuracoes/usuarios',
        module: SystemModule.USUARIOS
      },
      {
        id: 'config-sistema',
        label: 'Sistema',
        icon: 'fas fa-cogs',
        path: '/configuracoes/sistema',
        module: SystemModule.CONFIG_SISTEMA
      },
      {
        id: 'config-backup',
        label: 'Backup',
        icon: 'fas fa-database',
        path: '/configuracoes/backup',
        module: SystemModule.CONFIG_BACKUP
      },
      {
        id: 'config-integracoes',
        label: 'Integrações',
        icon: 'fas fa-plug',
        path: '/configuracoes/integracoes',
        module: SystemModule.CONFIG_INTEGRACOES
      },
      {
        id: 'config-whatsapp',
        label: 'WhatsApp',
        icon: 'fab fa-whatsapp',
        path: '/configuracoes/whatsapp',
        module: SystemModule.CONFIG_WHATSAPP
      },
      {
        id: 'config-whatsapp-contatos',
        label: 'Contatos Chatbot',
        icon: 'fas fa-user-slash',
        path: '/configuracoes/whatsapp/contatos-nao-identificados',
        module: SystemModule.CONFIG_WHATSAPP
      }
    ]
  }
];

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

/**
 * Componente Sidebar - Menu lateral de navegação
 *
 * @param collapsed - Estado de colapso do menu
 * @param onToggle - Função para alternar colapso
 * @param isMobile - Indica se está em modo mobile
 * @returns JSX.Element - Componente de menu lateral
 */
const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const { hasAccess, isAdmin } = usePermissions();
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Limpar timeout quando o componente desmontar
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // =============================================
  // VERIFICAÇÃO DE PERMISSÕES
  // =============================================

  /**
   * Verifica se o usuário tem permissão para ver um item do menu
   * @param item - Item do menu
   * @returns boolean - Se o usuário pode ver o item
   */
  const canViewMenuItem = (item: MenuItem | SubMenuItem): boolean => {
    // Se requer admin e não é admin, bloquear
    if ('requireAdmin' in item && item.requireAdmin && !isAdmin) {
      return false;
    }

    // Admin sempre vê todos os itens do menu
    if (isAdmin) {
      return true;
    }

    // Se não tem módulo definido, permitir (itens sem restrição)
    if (!item.module) {
      return true;
    }

    // Verificar se tem acesso ao módulo
    return hasAccess(item.module);
  };

  /**
   * Filtra subitens baseado em permissões
   * @param submenu - Array de subitens
   * @returns SubMenuItem[] - Subitens filtrados
   */
  const filterSubmenu = (submenu?: SubMenuItem[]): SubMenuItem[] | undefined => {
    if (!submenu) return undefined;
    const filtered = submenu.filter(canViewMenuItem);
    return filtered.length > 0 ? filtered : undefined;
  };

  /**
   * Filtra itens do menu principal baseado em permissões
   * @returns MenuItem[] - Itens filtrados
   */
  const getFilteredMenuItems = (): MenuItem[] => {
    return menuItems
      .map(item => ({
        ...item,
        submenu: filterSubmenu(item.submenu)
      }))
      .filter((item, index) => {
        const originalItem = menuItems[index];

        // Se o item original tinha submenu, verificar se ainda tem subitens após filtrar
        if (originalItem.submenu) {
          return item.submenu && item.submenu.length > 0;
        }

        // Se não tinha submenu originalmente, verificar permissão normal
        return canViewMenuItem(item);
      });
  };

  // Itens filtrados baseado em permissões
  const filteredMenuItems = getFilteredMenuItems();

  // =============================================
  // MANIPULAÇÃO DE MENUS
  // =============================================

  /**
   * Alterna expansão de submenu
   * @param menuId - ID do menu a ser expandido/recolhido
   */
  const toggleSubmenu = (menuId: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  /**
   * Mostra popover de submenu quando o menu está colapsado
   * @param menuId - ID do menu
   */
  const handleMenuHover = (menuId: string) => {
    // Cancelar timeout pendente se existir
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (collapsed && !isMobile) {
      setHoveredMenu(menuId);
    }
  };

  /**
   * Esconde popover de submenu com delay de 2,5 segundos
   */
  const handleMenuLeave = () => {
    if (collapsed && !isMobile) {
      // Agendar o fechamento do popover após 2,5 segundos
      hideTimeoutRef.current = setTimeout(() => {
        setHoveredMenu(null);
        hideTimeoutRef.current = null;
      }, 2500);
    }
  };

  /**
   * Navega para uma rota específica
   * @param path - Caminho da rota
   */
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  /**
   * Verifica se um item de menu está ativo
   * @param path - Caminho do item
   * @returns boolean - Se o item está ativo
   */
  const isActive = (path?: string): boolean => {
    if (!path) return false;
    // Suportar query params na comparação de caminhos
    const fullCurrent = location.pathname + location.search;
    if (path.includes('?')) {
      return fullCurrent === path;
    }
    return location.pathname === path;
  };

  /**
   * Verifica se um submenu contém algum item ativo
   * @param submenu - Array de subitens
   * @returns boolean - Se o submenu contém item ativo
   */
  const hasActiveSubmenu = (submenu?: SubMenuItem[]): boolean => {
    if (!submenu) return false;
    return submenu.some(item => isActive(item.path));
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {/* Cabeçalho do Sidebar */}
      <div className="sidebar-header">
        <button className="menu-toggle" onClick={onToggle} title="Alternar menu">
          <i className="fas fa-bars"></i>
        </button>

        {!collapsed ? (
          <div className="sidebar-logo">
            <img
              src="/images/logo-sidebar-white.png"
              alt="ConfereAí"
              className="logo-full"
            />
            <p>Sistema de Checklist</p>
          </div>
        ) : (
          <div className="sidebar-icon">
            <img
              src="/images/icon-sidebar.png"
              alt="ConfereAí"
              className="logo-icon"
            />
          </div>
        )}
      </div>

      {/* Menu de Navegação */}
      <div className="sidebar-menu">
        {filteredMenuItems.map((item) => {
          const isItemActive = isActive(item.path) || hasActiveSubmenu(item.submenu);
          const isExpanded = expandedMenus.has(item.id);

          return (
            <div
              key={item.id}
              className={`menu-item ${isItemActive ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
              onMouseEnter={() => item.submenu && handleMenuHover(item.id)}
              onMouseLeave={handleMenuLeave}
            >
              {/* Item Principal */}
              <button
                type="button"
                className={`sidebar-menu-btn ${isItemActive ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (item.submenu) {
                    if (collapsed && !isMobile) {
                      // No menu colapsado, o click também mostra o popover
                      handleMenuHover(item.id);
                    } else {
                      toggleSubmenu(item.id);
                    }
                  } else if (item.path) {
                    handleNavigation(item.path);
                  }
                }}
                title={collapsed && !isMobile ? item.label : ''}
              >
                <i className={item.icon}></i>
                {!collapsed && <span>{item.label}</span>}

                {item.submenu && !collapsed && (
                  <i className={`fas fa-chevron-down menu-arrow ${isExpanded ? 'expanded' : ''}`}></i>
                )}
              </button>

              {/* Submenu normal (quando não está colapsado ou em mobile) */}
              {item.submenu && (isMobile || !collapsed) && (
                <div className={`submenu ${isExpanded ? 'expanded' : ''}`}>
                  {item.submenu.map((subItem) => (
                    <button
                      type="button"
                      key={subItem.id}
                      onClick={() => {
                        handleNavigation(subItem.path);
                      }}
                      className={`sidebar-submenu-btn ${isActive(subItem.path) ? 'active' : ''}`}
                    >
                      <i className={subItem.icon}></i>
                      {subItem.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Popover de submenu (quando está colapsado e desktop) */}
              {item.submenu && collapsed && !isMobile && hoveredMenu === item.id && (
                <div
                  className="submenu-popover"
                  onMouseEnter={() => handleMenuHover(item.id)}
                  onMouseLeave={handleMenuLeave}
                >
                  <div className="popover-header">{item.label}</div>
                  {item.submenu.map((subItem) => (
                    <button
                      type="button"
                      key={subItem.id}
                      onClick={() => {
                        handleNavigation(subItem.path);
                        // Cancelar timeout e fechar imediatamente ao clicar
                        if (hideTimeoutRef.current) {
                          clearTimeout(hideTimeoutRef.current);
                          hideTimeoutRef.current = null;
                        }
                        setHoveredMenu(null);
                      }}
                      className={`sidebar-submenu-btn ${isActive(subItem.path) ? 'active' : ''}`}
                    >
                      <i className={subItem.icon}></i>
                      {subItem.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Rodapé do Sidebar (opcional) */}
      {!collapsed && (
        <div className="sidebar-footer">
          <div className="version-info">
            <small>v1.0.0</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
